import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Typography,
  Button,
  Space,
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Tag,
  Table,
  Modal,
  Spin,
  Divider,
  Row,
  Col,
  Popconfirm,
  Tooltip,
  AutoComplete,
  App,
} from 'antd';
import {
  ArrowLeftOutlined,
  PlusOutlined,
  DeleteOutlined,
  SaveOutlined,
  InfoCircleOutlined,
  SearchOutlined,
  DownloadOutlined,
  ThunderboltOutlined,
  LineChartOutlined,
  AimOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  Asset,
  AssetCategory,
  AssetPriceSource,
  AssetNetwork,
  AssetProvider,
  ProviderCapability,
  ProviderAssetLink,
  CreateProviderLinkRequest,
  UpdateProviderLinkRequest,
  IssuerAsset,
  RwaActivationChecklist,
} from '@mygate/common';
import { assetManagementService } from '@/services/asset-management';
import { RwaIssuerContentCard } from '@/components/RwaIssuerContentCard';

const { Title, Text } = Typography;
const { TextArea } = Input;

const infoIcon = (tip: string) => (
  <Tooltip title={tip}>
    <InfoCircleOutlined style={{ color: '#999', marginLeft: 4, cursor: 'help' }} />
  </Tooltip>
);

const CHAIN_DEFAULTS: Record<string, { chainId?: number; protocol: 'btc' | 'evm' }> = {
  bitcoin: { protocol: 'btc' },
  ethereum: { chainId: 1, protocol: 'evm' },
  polygon: { chainId: 137, protocol: 'evm' },
  bsc: { chainId: 56, protocol: 'evm' },
};

const PROVIDER_DEFAULT_CAPABILITIES: Record<string, ProviderCapability[]> = {
  [AssetProvider.TANGANY]: [ProviderCapability.CUSTODY, ProviderCapability.SEND_RECEIVE],
  [AssetProvider.TRADEVEST]: [ProviderCapability.TRADING, ProviderCapability.PRICE_FEED],
  [AssetProvider.SELF_CUSTODY]: [ProviderCapability.CUSTODY, ProviderCapability.SEND_RECEIVE],
};

const PROVIDER_SYMBOL_HINTS: Record<string, string> = {
  [AssetProvider.TANGANY]: "Tangany custody symbol (uppercase: BTC, ETH). Must match Tangany's supported asset list.",
  [AssetProvider.TRADEVEST]: 'Tradevest API symbol. Use "Import from Tradevest" for auto-population.',
  [AssetProvider.SELF_CUSTODY]: 'On-chain token symbol used by blockchain explorers (uppercase: BTC, ETH).',
};

const PROVIDER_USES_NETWORKS = new Set([AssetProvider.TANGANY, AssetProvider.SELF_CUSTODY]);

const EXCHANGE_PRIORITY = ['KRAKEN', 'BINANCE', 'COINBASE', 'BITSTAMP'];

async function resolveTradingViewSymbol(symbol: string): Promise<string | null> {
  try {
    const res = await assetManagementService.searchTradingView(`${symbol}EUR`);
    const results = res.data || [];
    if (results.length === 0) return null;

    for (const exchange of EXCHANGE_PRIORITY) {
      const match = results.find(r => r.exchange === exchange);
      if (match) return match.symbol;
    }

    return results[0].symbol;
  } catch {
    return null;
  }
}

// ─── Search Hooks with Debounce, Cancellation, and Error Handling ───

interface AutoCompleteOption {
  value: string;
  label: React.ReactNode;
  symbol?: string;
}

function useCoinPaprikaSearch() {
  const [options, setOptions] = useState<AutoCompleteOption[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const abortRef = useRef<AbortController>(undefined);

  const search = useCallback((text: string) => {
    clearTimeout(timerRef.current);
    abortRef.current?.abort();

    if (text.length < 2) {
      setOptions([]);
      return;
    }

    timerRef.current = setTimeout(() => {
      abortRef.current = new AbortController();
      assetManagementService
        .searchCoinPaprika(text)
        .then(res => {
          if (abortRef.current?.signal.aborted) return;
          setOptions(
            (res.data || []).map(c => ({
              value: c.id,
              label: (
                <Space>
                  <strong>{c.symbol}</strong>
                  <span>{c.name}</span>
                  <Tag style={{ fontSize: 11 }}>#{c.rank}</Tag>
                </Space>
              ),
              symbol: c.symbol,
            })),
          );
        })
        .catch(() => {
          if (!abortRef.current?.signal.aborted) setOptions([]);
        });
    }, 300);
  }, []);

  useEffect(
    () => () => {
      clearTimeout(timerRef.current);
      abortRef.current?.abort();
    },
    [],
  );

  return { options, search };
}

function useTradingViewSearch() {
  const [options, setOptions] = useState<AutoCompleteOption[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const abortRef = useRef<AbortController>(undefined);

  const search = useCallback((text: string) => {
    clearTimeout(timerRef.current);
    abortRef.current?.abort();

    if (text.length < 2) {
      setOptions([]);
      return;
    }

    timerRef.current = setTimeout(() => {
      abortRef.current = new AbortController();
      assetManagementService
        .searchTradingView(text)
        .then(res => {
          if (abortRef.current?.signal.aborted) return;
          setOptions(
            (res.data || []).map(s => ({
              value: s.symbol,
              label: (
                <Space>
                  <strong>{s.symbol}</strong>
                  <span style={{ color: '#888' }}>{s.description}</span>
                </Space>
              ),
            })),
          );
        })
        .catch(() => {
          if (!abortRef.current?.signal.aborted) setOptions([]);
        });
    }, 300);
  }, []);

  useEffect(
    () => () => {
      clearTimeout(timerRef.current);
      abortRef.current?.abort();
    },
    [],
  );

  return { options, search };
}

// ─── Pricing Section with Autocomplete ───

function PricingSection({ form }: { form: ReturnType<typeof Form.useForm>[0] }) {
  const priceSource = Form.useWatch('priceSource', form) as AssetPriceSource | undefined;
  const fallbackPriceSource = Form.useWatch('fallbackPriceSource', form) as AssetPriceSource | undefined;
  const watchedPriceSymbol = Form.useWatch('priceSymbol', form) as string | undefined;
  const watchedTradingViewSymbol = Form.useWatch('tradingViewSymbol', form) as string | undefined;
  const watchedSymbol = Form.useWatch('symbol', form) as string | undefined;
  const [tvResolving, setTvResolving] = useState(false);
  const [testingPrice, setTestingPrice] = useState(false);
  const [priceResult, setPriceResult] = useState<{ ok: boolean; price?: number; error?: string } | null>(null);
  const [tvPreviewOpen, setTvPreviewOpen] = useState(false);
  const testPriceAbortRef = useRef<AbortController>(undefined);

  const cpSearch = useCoinPaprikaSearch();
  const tvSearch = useTradingViewSearch();
  const fbCpSearch = useCoinPaprikaSearch();

  useEffect(() => {
    setPriceResult(null);
  }, [priceSource, watchedPriceSymbol]);

  const handleTestPrice = useCallback(async () => {
    testPriceAbortRef.current?.abort();
    testPriceAbortRef.current = new AbortController();
    const signal = testPriceAbortRef.current.signal;

    const src = form.getFieldValue('priceSource') as AssetPriceSource;
    const sym =
      src === AssetPriceSource.TRADEVEST
        ? (form.getFieldValue('symbol') as string)
        : (form.getFieldValue('priceSymbol') as string | undefined);
    setTestingPrice(true);
    setPriceResult(null);
    try {
      const res = await assetManagementService.testPrice(src, sym);
      if (signal.aborted) return;
      setPriceResult({ ok: true, price: res.data!.priceEur });
    } catch (err) {
      if (signal.aborted) return;
      setPriceResult({ ok: false, error: (err as { message?: string }).message || 'Failed to fetch price' });
    } finally {
      if (!signal.aborted) setTestingPrice(false);
    }
  }, [form]);

  const handleResolveTradingView = useCallback(async () => {
    const symbol = form.getFieldValue('symbol') as string | undefined;
    if (!symbol) return;
    setTvResolving(true);
    const resolved = await resolveTradingViewSymbol(symbol);
    form.setFieldValue('tradingViewSymbol', resolved ?? `KRAKEN:${symbol}EUR`);
    setTvResolving(false);
  }, [form]);

  const showPriceSymbol = priceSource === AssetPriceSource.COINPAPRIKA;
  const showTradingView = priceSource !== AssetPriceSource.MANUAL;
  const showFallbackSection = priceSource !== AssetPriceSource.MANUAL;
  const showFallbackSymbol = fallbackPriceSource === AssetPriceSource.COINPAPRIKA;

  const handleCpSelect = useCallback(
    (value: string, option: AutoCompleteOption) => {
      form.setFieldValue('priceSymbol', value);
      const currentTV = form.getFieldValue('tradingViewSymbol');
      if (!currentTV && option.symbol) {
        setTvResolving(true);
        resolveTradingViewSymbol(option.symbol).then(resolved => {
          if (!form.getFieldValue('tradingViewSymbol')) {
            form.setFieldValue('tradingViewSymbol', resolved ?? `KRAKEN:${option.symbol}EUR`);
          }
          setTvResolving(false);
        });
      }
    },
    [form],
  );

  const handlePriceSourceChange = useCallback(
    (value: AssetPriceSource) => {
      if (value === AssetPriceSource.MANUAL) {
        form.setFieldValue('priceSymbol', undefined);
        form.setFieldValue('tradingViewSymbol', undefined);
        form.setFieldValue('fallbackPriceSource', undefined);
        form.setFieldValue('fallbackPriceSymbol', undefined);
      } else if (value === AssetPriceSource.TRADEVEST) {
        form.setFieldValue('priceSymbol', undefined);
      }
    },
    [form],
  );

  const fallbackSources = useMemo(() => Object.values(AssetPriceSource).filter(s => s !== priceSource), [priceSource]);

  return (
    <>
      <Row gutter={16} align="bottom">
        <Col xs={24} sm={12} xl={6}>
          <Form.Item
            name="priceSource"
            label={<>Price Source {infoIcon('Primary API for fetching live prices (used by backend price service)')}</>}
            rules={[{ required: true, message: 'Price source is required' }]}
          >
            <Select onChange={handlePriceSourceChange}>
              {Object.values(AssetPriceSource).map(src => (
                <Select.Option key={src} value={src}>
                  {src}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        {showPriceSymbol && (
          <Col xs={24} sm={12} xl={8}>
            <Form.Item
              name="priceSymbol"
              label={
                <>
                  CoinPaprika ID {infoIcon('Search for the asset to get the correct CoinPaprika ID (e.g. btc-bitcoin)')}
                </>
              }
            >
              <AutoComplete
                options={cpSearch.options}
                onSearch={cpSearch.search}
                onSelect={(val, opt) => handleCpSelect(val, opt as AutoCompleteOption)}
                placeholder="Search CoinPaprika..."
                suffixIcon={<SearchOutlined />}
                aria-label="Search CoinPaprika for asset identifier"
                notFoundContent="No results. Type at least 2 characters."
              />
            </Form.Item>
          </Col>
        )}

        <Col>
          <Form.Item className="form-inline-action" label=" ">
            <Space>
              <Button
                onClick={handleTestPrice}
                loading={testingPrice}
                disabled={
                  (priceSource === AssetPriceSource.COINPAPRIKA && !watchedPriceSymbol) ||
                  (priceSource === AssetPriceSource.TRADEVEST && !watchedSymbol)
                }
                icon={<ThunderboltOutlined />}
                size="small"
              >
                Test Price
              </Button>
              {priceResult && (
                <Tag color={priceResult.ok ? 'success' : 'error'}>
                  {priceResult.ok
                    ? `EUR ${priceResult.price?.toFixed(priceResult.price >= 1 ? 2 : 6)}`
                    : priceResult.error}
                </Tag>
              )}
            </Space>
          </Form.Item>
        </Col>
      </Row>

      {showTradingView && (
        <Row gutter={16} align="bottom">
          <Col xs={24} sm={12} xl={8}>
            <Form.Item
              name="tradingViewSymbol"
              label={<>TradingView Symbol {infoIcon('Used for price charts in mobile app (e.g. KRAKEN:BTCEUR)')}</>}
            >
              <AutoComplete
                options={tvSearch.options}
                onSearch={tvSearch.search}
                placeholder={tvResolving ? 'Auto-resolving...' : 'Search TradingView...'}
                suffixIcon={tvResolving ? <Spin size="small" /> : <SearchOutlined />}
                disabled={tvResolving}
                aria-label="Search TradingView for chart symbol"
                notFoundContent="No results. Type at least 2 characters."
              />
            </Form.Item>
          </Col>
          <Col>
            <Form.Item className="form-inline-action" label=" ">
              <Space>
                <Tooltip title="Auto-resolve from asset symbol (searches {SYMBOL}EUR on TradingView)">
                  <Button
                    icon={<AimOutlined />}
                    size="small"
                    loading={tvResolving}
                    disabled={!watchedSymbol}
                    onClick={handleResolveTradingView}
                  >
                    Auto-resolve
                  </Button>
                </Tooltip>
                {watchedTradingViewSymbol && (
                  <Button onClick={() => setTvPreviewOpen(true)} icon={<LineChartOutlined />} size="small">
                    Preview Chart
                  </Button>
                )}
              </Space>
            </Form.Item>
          </Col>
        </Row>
      )}

      {showFallbackSection && (
        <Row gutter={16}>
          <Col xs={24} sm={12} xl={5}>
            <Form.Item
              name="fallbackPriceSource"
              label={<>Fallback Source {infoIcon('Used when primary price source is unavailable')}</>}
            >
              <Select allowClear placeholder="None">
                {fallbackSources.map(src => (
                  <Select.Option key={src} value={src}>
                    {src}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          {showFallbackSymbol && (
            <Col xs={24} sm={12} xl={8}>
              <Form.Item
                name="fallbackPriceSymbol"
                label={<>Fallback CoinPaprika ID {infoIcon('CoinPaprika ID for fallback pricing')}</>}
              >
                <AutoComplete
                  options={fbCpSearch.options}
                  onSearch={fbCpSearch.search}
                  placeholder="Search CoinPaprika..."
                  suffixIcon={<SearchOutlined />}
                  aria-label="Search CoinPaprika for fallback asset identifier"
                  notFoundContent="No results. Type at least 2 characters."
                />
              </Form.Item>
            </Col>
          )}
          <Col xs={24} sm={12} xl={4}>
            <Form.Item
              name="fallbackPrice"
              label={<>Fallback Price (EUR) {infoIcon('Static EUR price used when all price sources fail')}</>}
            >
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
      )}

      {priceSource === AssetPriceSource.MANUAL && (
        <Row gutter={16}>
          <Col xs={24} sm={12} xl={4}>
            <Form.Item name="fallbackPrice" label={<>Price (EUR) {infoIcon('Static EUR price for this asset')}</>}>
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
      )}

      <Modal
        title={`Chart: ${watchedTradingViewSymbol}`}
        open={tvPreviewOpen}
        onCancel={() => setTvPreviewOpen(false)}
        footer={null}
        width={700}
        destroyOnClose
      >
        {watchedTradingViewSymbol && (
          <iframe
            srcDoc={`<!DOCTYPE html><html><head><style>body,html{margin:0;padding:0;overflow:hidden}</style></head><body><div class="tradingview-widget-container"><div class="tradingview-widget-container__widget"></div><script src="https://s3.tradingview.com/external-embedding/embed-widget-mini-symbol-overview.js" async>{"symbol":"${watchedTradingViewSymbol}","width":"100%","height":400,"locale":"en","dateRange":"1M","colorTheme":"light","isTransparent":false}</script></div></body></html>`}
            style={{ width: '100%', height: 420, border: 'none' }}
            sandbox="allow-scripts allow-same-origin"
            title="TradingView Chart Preview"
          />
        )}
      </Modal>
    </>
  );
}

export function AssetDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { message } = App.useApp();
  const isNew = !id || id === 'new';

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [asset, setAsset] = useState<Asset | null>(null);
  const [providers, setProviders] = useState<ProviderAssetLink[]>([]);
  const [providerModalVisible, setProviderModalVisible] = useState(false);
  const [editingLink, setEditingLink] = useState<ProviderAssetLink | null>(null);
  const [providerForm] = Form.useForm();
  const [providerSaving, setProviderSaving] = useState(false);
  const [fetchingIcon, setFetchingIcon] = useState(false);
  const [logoPreviewError, setLogoPreviewError] = useState(false);
  const [iconDialogOpen, setIconDialogOpen] = useState(false);
  const [iconSearchValue, setIconSearchValue] = useState('');
  const [providerNetworks, setProviderNetworks] = useState<AssetNetwork[]>([]);
  const [selectedProviderId, setSelectedProviderId] = useState<AssetProvider | undefined>(undefined);
  const iconCpSearch = useCoinPaprikaSearch();

  const watchedLogoUrl = Form.useWatch('logoUrl', form) as string | undefined;
  const watchedSymbol = Form.useWatch('symbol', form) as string | undefined;

  const handleIconCpSelect = useCallback(
    (coinId: string) => {
      setIconDialogOpen(false);
      setIconSearchValue('');
      setFetchingIcon(true);

      const url = `https://static.coinpaprika.com/coin/${coinId}/logo.png`;
      const img = new window.Image();
      img.onload = () => {
        form.setFieldValue('logoUrl', url);
        setLogoPreviewError(false);
        setFetchingIcon(false);
        form.submit();
      };
      img.onerror = () => {
        setFetchingIcon(false);
        message.error(`Failed to fetch icon for "${coinId}"`);
      };
      img.src = url;
    },
    [form, message],
  );

  const [issuerAsset, setIssuerAsset] = useState<IssuerAsset | null>(null);
  const [rwaChecklist, setRwaChecklist] = useState<RwaActivationChecklist | undefined>();
  const watchedCategory = Form.useWatch('category', form) as AssetCategory | undefined;

  /**
   * `resetForm: false` reloads the asset and its checklist without writing the server copy back into
   * the form — the issuer-content card refetches after every link/unlink, and the admin has usually
   * just picked a hero image or unticked sections that are not saved yet.
   */
  const fetchAsset = useCallback(
    async (resetForm = true) => {
      if (isNew || !id) return;
      setLoading(true);
      try {
        const response = await assetManagementService.getAsset(id);
        if (response.data) {
          setAsset(response.data.asset);
          setProviders(response.data.providers);
          setIssuerAsset(response.data.issuerAsset ?? null);
          setRwaChecklist(response.data.rwaChecklist);
          if (resetForm) {
            form.setFieldsValue({
              ...response.data.asset,
              tags: response.data.asset.tags?.join(', ') || '',
            });
          }
        }
      } catch {
        message.error('Failed to load asset');
        navigate('/assets');
      } finally {
        setLoading(false);
      }
    },
    [id, isNew, form, message, navigate],
  );

  useEffect(() => {
    fetchAsset();
  }, [fetchAsset]);

  const handleSave = async (values: Record<string, unknown>) => {
    setSaving(true);
    try {
      const tags =
        typeof values.tags === 'string'
          ? (values.tags as string)
              .split(',')
              .map(t => t.trim())
              .filter(Boolean)
          : [];

      const payload = {
        ...values,
        tags,
      };

      if (isNew) {
        const response = await assetManagementService.createAsset(
          payload as unknown as Parameters<typeof assetManagementService.createAsset>[0],
        );
        if (response.data) {
          message.success('Asset created');
          navigate(`/assets/${response.data._id}`);
        }
      } else if (id) {
        const response = await assetManagementService.updateAsset(
          id,
          payload as unknown as Parameters<typeof assetManagementService.updateAsset>[1],
        );
        if (response.data) {
          setAsset(response.data);
          message.success('Asset updated');
          // The activation checklist depends on what was just saved (hero, category, link) — reload it.
          void fetchAsset(true);
        }
      }
    } catch (error) {
      // Server-side reasons matter here: an RWA activation refusal names the exact blockers.
      const reason = error instanceof Error && error.message ? error.message : '';
      message.error(reason || `Failed to ${isNew ? 'create' : 'update'} asset`);
    } finally {
      setSaving(false);
    }
  };

  // --- Provider link management ---
  const openProviderModal = (link?: ProviderAssetLink) => {
    if (link) {
      setEditingLink(link);
      setSelectedProviderId(link.providerId);
      setProviderNetworks(link.networks || []);
      providerForm.setFieldsValue({
        providerId: link.providerId,
        providerSpecificSymbol: link.providerSpecificSymbol,
        capabilities: link.capabilities,
        isEnabled: link.isEnabled,
        maxWithdrawalAmount: link.feeConfig?.maxWithdrawalAmount,
      });
    } else {
      setEditingLink(null);
      setSelectedProviderId(undefined);
      setProviderNetworks([]);
      providerForm.resetFields();
      providerForm.setFieldsValue({ isEnabled: true });
    }
    setProviderModalVisible(true);
  };

  const handleProviderSelect = (provider: AssetProvider) => {
    setSelectedProviderId(provider);
    const defaults = PROVIDER_DEFAULT_CAPABILITIES[provider];
    if (defaults && !editingLink) {
      providerForm.setFieldValue('capabilities', defaults);
    }
    // Clear networks when switching to a provider that doesn't use them
    if (!PROVIDER_USES_NETWORKS.has(provider)) {
      setProviderNetworks([]);
    }
  };

  const addProviderNetwork = () => {
    setProviderNetworks(prev => [...prev, { chain: '', protocol: 'evm', isDefault: prev.length === 0 }]);
  };

  const updateProviderNetwork = (index: number, field: keyof AssetNetwork, value: unknown) => {
    setProviderNetworks(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'chain' && typeof value === 'string' && CHAIN_DEFAULTS[value]) {
        updated[index] = { ...updated[index], ...CHAIN_DEFAULTS[value], chain: value };
      }
      if (field === 'isDefault' && value === true) {
        updated.forEach((n, i) => {
          if (i !== index) n.isDefault = false;
        });
      }
      return updated;
    });
  };

  const removeProviderNetwork = (index: number) => {
    setProviderNetworks(prev => prev.filter((_, i) => i !== index));
  };

  const handleProviderSave = async (values: Record<string, unknown>) => {
    if (!id || isNew) return;
    setProviderSaving(true);
    try {
      const feeConfig = {
        maxWithdrawalAmount: values.maxWithdrawalAmount as number | undefined,
      };

      if (editingLink) {
        const updateData: UpdateProviderLinkRequest = {
          providerSpecificSymbol: values.providerSpecificSymbol as string,
          capabilities: values.capabilities as ProviderCapability[],
          isEnabled: values.isEnabled as boolean,
          networks: providerNetworks,
          feeConfig,
        };
        await assetManagementService.updateProviderLink(id, editingLink._id, updateData);
        message.success('Provider link updated');
      } else {
        const createData: CreateProviderLinkRequest = {
          providerId: values.providerId as AssetProvider,
          providerSpecificSymbol: values.providerSpecificSymbol as string,
          capabilities: values.capabilities as ProviderCapability[],
          isEnabled: values.isEnabled as boolean | undefined,
          networks: providerNetworks,
          feeConfig,
        };
        await assetManagementService.addProviderLink(id, createData);
        message.success('Provider link added');
      }

      setProviderModalVisible(false);
      fetchAsset();
    } catch {
      message.error(`Failed to ${editingLink ? 'update' : 'add'} provider link`);
    } finally {
      setProviderSaving(false);
    }
  };

  const handleDeleteProvider = async (linkId: string) => {
    if (!id) return;
    try {
      await assetManagementService.deleteProviderLink(id, linkId);
      message.success('Provider link deleted');
      fetchAsset();
    } catch {
      message.error('Failed to delete provider link');
    }
  };

  const providerColumns: ColumnsType<ProviderAssetLink> = [
    {
      title: 'Provider',
      dataIndex: 'providerId',
      key: 'providerId',
      width: 140,
      render: provider => <Tag color="blue">{provider}</Tag>,
    },
    {
      title: 'Symbol',
      dataIndex: 'providerSpecificSymbol',
      key: 'providerSpecificSymbol',
      width: 100,
    },
    {
      title: 'Capabilities',
      dataIndex: 'capabilities',
      key: 'capabilities',
      render: (caps: ProviderCapability[]) => (
        <Space size={4} wrap>
          {caps.map(c => (
            <Tag key={c} style={{ fontSize: 11 }}>
              {c}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'Networks',
      dataIndex: 'networks',
      key: 'networks',
      render: (networks: AssetNetwork[]) => (
        <Space size={4} wrap>
          {(networks || []).map(n => (
            <Tag key={n.chain} color="cyan">
              {n.chain}
            </Tag>
          ))}
          {(!networks || networks.length === 0) && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              —
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Enabled',
      dataIndex: 'isEnabled',
      key: 'isEnabled',
      width: 80,
      align: 'center',
      render: enabled => <Tag color={enabled ? 'green' : 'default'}>{enabled ? 'Yes' : 'No'}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size={4}>
          <Button type="link" size="small" onClick={() => openProviderModal(record)}>
            Edit
          </Button>
          <Popconfirm title="Delete this provider link?" onConfirm={() => handleDeleteProvider(record._id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 240 }}>
        <Spin size="large" />
      </div>
    );
  }

  const isRwa = (watchedCategory ?? asset?.category) === AssetCategory.RWA;
  // Blocked = an RWA asset that is currently inactive and whose checklist says no. Deactivation is always allowed.
  const activationBlocked =
    isRwa && !isNew && !asset?.isActive && rwaChecklist !== undefined && !rwaChecklist.canActivate;

  return (
    <>
      <div className="page-toolbar">
        <Space align="center">
          <Button icon={<ArrowLeftOutlined />} type="text" onClick={() => navigate('/assets')} />
          {!isNew && asset?.logoUrl && (
            <img
              src={asset.logoUrl}
              alt={asset.symbol}
              style={{ width: 28, height: 28, borderRadius: 14, objectFit: 'cover', verticalAlign: 'middle' }}
              onError={e => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          )}
          <Title level={4} style={{ margin: 0, lineHeight: 1 }}>
            {isNew ? 'Create Asset' : `${asset?.symbol ?? ''} - ${asset?.name ?? ''}`}
          </Title>
          {asset && <Tag color={asset.isActive ? 'green' : 'default'}>{asset.isActive ? 'Active' : 'Inactive'}</Tag>}
        </Space>
      </div>

      <div style={{ maxWidth: 1200 }}>
        {/* Asset Form */}
        <Card title="Asset Details" style={{ marginBottom: 16 }}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSave}
            initialValues={{
              category: AssetCategory.CRYPTO,
              priceSource: AssetPriceSource.COINPAPRIKA,
              decimals: 8,
              placement: 999,
              isActive: true,
              logoUrl: '',
              tags: '',
            }}
          >
            <Row gutter={16}>
              <Col xs={24} sm={12} xl={6}>
                <Form.Item
                  name="symbol"
                  label={
                    <>Symbol {infoIcon('Unique asset identifier. Used everywhere: mobile app, trading, wallets')}</>
                  }
                  rules={[{ required: true, message: 'Symbol is required' }]}
                >
                  <Input placeholder="BTC" style={{ textTransform: 'uppercase' }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} xl={10}>
                <Form.Item
                  name="name"
                  label={<>Name {infoIcon('Full asset name. Displayed in mobile app asset lists and trade screens')}</>}
                  rules={[{ required: true, message: 'Name is required' }]}
                >
                  <Input placeholder="Bitcoin" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} xl={4}>
                <Form.Item
                  name="category"
                  label={<>Category {infoIcon('Groups assets into tabs in mobile app (Crypto, ETF, Commodity)')}</>}
                  rules={[{ required: true, message: 'Category is required' }]}
                >
                  <Select>
                    {Object.values(AssetCategory).map(cat => (
                      <Select.Option key={cat} value={cat}>
                        {cat}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} xl={4}>
                <Form.Item
                  name="decimals"
                  label={
                    <>Decimals {infoIcon('Decimal precision for amounts. Used in mobile and backend formatting')}</>
                  }
                  rules={[{ required: true, message: 'Decimals is required' }]}
                >
                  <InputNumber min={0} max={18} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16} align="bottom">
              <Col xs={24} sm={12} xl={10}>
                <Form.Item
                  name="logoUrl"
                  label={<>Logo URL {infoIcon('Asset logo image. Shown in mobile app lists and trade screens')}</>}
                >
                  <Input placeholder="https://..." onChange={() => setLogoPreviewError(false)} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} xl={6}>
                <Form.Item className="form-inline-action" label=" ">
                  <Space>
                    <Button icon={<DownloadOutlined />} onClick={() => setIconDialogOpen(true)} loading={fetchingIcon}>
                      Fetch Icon
                    </Button>
                    {watchedLogoUrl && !logoPreviewError ? (
                      <a href={watchedLogoUrl} target="_blank" rel="noopener noreferrer">
                        <img
                          src={watchedLogoUrl}
                          alt="Logo preview"
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: 16,
                            objectFit: 'cover',
                            border: '1px solid #d9d9d9',
                          }}
                          onError={() => setLogoPreviewError(true)}
                        />
                      </a>
                    ) : watchedLogoUrl && logoPreviewError ? (
                      <Tooltip title="Failed to load image">
                        <div
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: 16,
                            background: '#fff1f0',
                            border: '1px solid #ffa39e',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 11,
                            fontWeight: 600,
                            color: '#cf1322',
                          }}
                        >
                          {(watchedSymbol || '??').slice(0, 2)}
                        </div>
                      </Tooltip>
                    ) : null}
                  </Space>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} xl={4}>
                <Form.Item
                  name="placement"
                  label={<>Placement {infoIcon('Sort order in asset lists. Lower number = higher position')}</>}
                >
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} sm={12} xl={16}>
                <Form.Item
                  name="description"
                  label={
                    <>
                      Description {infoIcon('Shown in mobile app on trade screen and portfolio view under asset name')}
                    </>
                  }
                >
                  <TextArea rows={2} placeholder="Short description of the asset" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} xl={8}>
                <Form.Item
                  name="tags"
                  label={
                    <>Tags (comma-separated) {infoIcon('Internal tags for staff use only. Not shown in mobile app')}</>
                  }
                >
                  <Input placeholder="defi, layer1, stablecoin" />
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left">Pricing</Divider>

            <PricingSection form={form} />

            {/* RWA overrides live in the form so they save with the asset; edited from the Issuer content card. */}
            <Form.Item name={['rwa', 'heroImageUrl']} hidden>
              <Input />
            </Form.Item>
            {/* Objects, not strings — Select cannot round-trip them, so the value rides the form alone. */}
            <Form.Item name={['rwa', 'hiddenSections']} hidden>
              <Input type="hidden" />
            </Form.Item>
            <Form.Item name={['rwa', 'sectionsNeedReview']} hidden>
              <Input type="hidden" />
            </Form.Item>

            <Row gutter={16} align="middle">
              <Col>
                <Form.Item
                  name="isActive"
                  label={
                    <>
                      Active{' '}
                      {infoIcon(
                        isRwa
                          ? 'RWA assets can only be activated once the issuer-content checklist below passes'
                          : 'Only active assets are visible to users in the mobile app',
                      )}
                    </>
                  }
                  valuePropName="checked"
                >
                  <Switch disabled={activationBlocked} />
                </Form.Item>
              </Col>
              {isRwa && (
                <Col>
                  <Form.Item
                    name={['rwa', 'sellAllowed']}
                    label={
                      <>
                        Sell allowed{' '}
                        {infoIcon(
                          'Shows "Verkaufen" for this token in the app. Switching it OFF also ' +
                            'refuses every sell order on the server, including from customers who ' +
                            'already hold it — leave it untouched unless the issuer stops redeeming.',
                        )}
                      </>
                    }
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
              )}
              <Col flex="auto" style={{ textAlign: 'right' }}>
                <Space>
                  <Button onClick={() => navigate('/assets')}>Cancel</Button>
                  <Button type="primary" htmlType="submit" loading={saving} icon={<SaveOutlined />}>
                    {isNew ? 'Create Asset' : 'Save Changes'}
                  </Button>
                </Space>
              </Col>
            </Row>
          </Form>
        </Card>

        {/* Provider Links */}
        {!isNew && (
          <Card
            title="Provider Links"
            extra={
              <Button type="dashed" icon={<PlusOutlined />} onClick={() => openProviderModal()} size="small">
                Add Provider
              </Button>
            }
          >
            <Table
              scroll={{ x: 720 }}
              columns={providerColumns}
              dataSource={providers}
              rowKey="_id"
              pagination={false}
              size="small"
              locale={{ emptyText: 'No provider links. Click "Add Provider" to connect a provider.' }}
            />
          </Card>
        )}

        {/* Issuer content + activation checklist (RWA only) */}
        {!isNew && isRwa && id && (
          <RwaIssuerContentCard
            assetId={id}
            issuerAsset={issuerAsset}
            checklist={rwaChecklist}
            form={form}
            onChanged={() => fetchAsset(false)}
          />
        )}
      </div>

      {/* Provider Link Modal */}
      <Modal
        title={editingLink ? 'Edit Provider Link' : 'Add Provider Link'}
        open={providerModalVisible}
        onCancel={() => setProviderModalVisible(false)}
        footer={null}
        width={750}
      >
        <Form form={providerForm} layout="vertical" onFinish={handleProviderSave}>
          {(() => {
            const showNetworks = selectedProviderId && PROVIDER_USES_NETWORKS.has(selectedProviderId);
            const symbolHint = selectedProviderId ? PROVIDER_SYMBOL_HINTS[selectedProviderId] : undefined;

            return (
              <>
                <Row gutter={16}>
                  <Col xs={24} sm={12} xl={12}>
                    <Form.Item
                      name="providerId"
                      label="Provider"
                      rules={[{ required: true, message: 'Provider is required' }]}
                    >
                      <Select disabled={!!editingLink} onChange={handleProviderSelect}>
                        {Object.values(AssetProvider).map(p => (
                          <Select.Option key={p} value={p}>
                            {p}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} xl={12}>
                    <Form.Item
                      name="providerSpecificSymbol"
                      label={<>Provider Symbol {symbolHint && infoIcon(symbolHint)}</>}
                      rules={[{ required: true, message: 'Symbol is required' }]}
                    >
                      <Input placeholder="BTC" />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="capabilities"
                  label="Capabilities"
                  rules={[{ required: true, message: 'At least one capability is required' }]}
                >
                  <Select mode="multiple" placeholder="Select capabilities">
                    {Object.values(ProviderCapability).map(c => (
                      <Select.Option key={c} value={c}>
                        {c}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item name="isEnabled" label="Enabled" valuePropName="checked">
                  <Switch />
                </Form.Item>

                {showNetworks && (
                  <>
                    <Divider orientation="left" plain>
                      Networks{' '}
                      {infoIcon(
                        'Blockchain networks this provider supports for this asset. ' +
                          'Required for custody and send/receive operations.',
                      )}
                    </Divider>
                    {providerNetworks.length === 0 ? (
                      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                        No networks configured.
                      </Text>
                    ) : (
                      <Space direction="vertical" size={8} style={{ width: '100%', marginBottom: 12 }}>
                        {providerNetworks.map((net, i) => (
                          <Card key={i} size="small" style={{ background: '#fafafa' }}>
                            <Row gutter={8} align="middle">
                              <Col xs={24} sm={12} xl={5}>
                                <Text type="secondary" style={{ fontSize: 11 }}>
                                  Chain
                                </Text>
                                <Select
                                  value={net.chain || undefined}
                                  onChange={v => updateProviderNetwork(i, 'chain', v)}
                                  style={{ width: '100%' }}
                                  placeholder="Select"
                                  size="small"
                                >
                                  <Select.Option value="bitcoin">Bitcoin</Select.Option>
                                  <Select.Option value="ethereum">Ethereum</Select.Option>
                                  <Select.Option value="polygon">Polygon</Select.Option>
                                  <Select.Option value="bsc">BSC</Select.Option>
                                </Select>
                              </Col>
                              <Col xs={24} sm={12} xl={4}>
                                <Text type="secondary" style={{ fontSize: 11 }}>
                                  Chain ID
                                </Text>
                                <InputNumber
                                  value={net.chainId}
                                  onChange={v => updateProviderNetwork(i, 'chainId', v)}
                                  style={{ width: '100%' }}
                                  placeholder="1"
                                  size="small"
                                />
                              </Col>
                              <Col xs={24} sm={12} xl={4}>
                                <Text type="secondary" style={{ fontSize: 11 }}>
                                  Protocol
                                </Text>
                                <Select
                                  value={net.protocol}
                                  onChange={v => updateProviderNetwork(i, 'protocol', v)}
                                  style={{ width: '100%' }}
                                  size="small"
                                >
                                  <Select.Option value="evm">EVM</Select.Option>
                                  <Select.Option value="btc">BTC</Select.Option>
                                </Select>
                              </Col>
                              <Col xs={24} sm={12} xl={7}>
                                <Text type="secondary" style={{ fontSize: 11 }}>
                                  Contract
                                </Text>
                                <Input
                                  value={net.contractAddress || ''}
                                  onChange={e =>
                                    updateProviderNetwork(i, 'contractAddress', e.target.value || undefined)
                                  }
                                  placeholder="0x..."
                                  size="small"
                                />
                                {net.protocol === 'evm' && !net.contractAddress && net.chain && (
                                  <Text type="warning" style={{ fontSize: 10 }}>
                                    <WarningOutlined /> Native token balance ({net.chain})
                                  </Text>
                                )}
                              </Col>
                              <Col xs={24} sm={12} xl={2}>
                                <Text type="secondary" style={{ fontSize: 11 }}>
                                  Default
                                </Text>
                                <div>
                                  <Switch
                                    checked={net.isDefault}
                                    onChange={v => updateProviderNetwork(i, 'isDefault', v)}
                                    size="small"
                                  />
                                </div>
                              </Col>
                              <Col xs={24} sm={12} xl={2} style={{ textAlign: 'right' }}>
                                <Button
                                  type="text"
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={() => removeProviderNetwork(i)}
                                  size="small"
                                />
                              </Col>
                            </Row>
                          </Card>
                        ))}
                      </Space>
                    )}
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={addProviderNetwork}
                      size="small"
                      block
                      style={{ marginBottom: 16 }}
                    >
                      Add Network
                    </Button>
                  </>
                )}

                <Divider orientation="left" plain>
                  Limits
                </Divider>

                <Row gutter={16}>
                  <Col xs={24} sm={12} xl={8}>
                    <Form.Item name="maxWithdrawalAmount" label="Max Withdrawal (EUR)">
                      <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                </Row>

                <div style={{ textAlign: 'right' }}>
                  <Space>
                    <Button onClick={() => setProviderModalVisible(false)}>Cancel</Button>
                    <Button type="primary" htmlType="submit" loading={providerSaving}>
                      {editingLink ? 'Update' : 'Add'} Provider Link
                    </Button>
                  </Space>
                </div>
              </>
            );
          })()}
        </Form>
      </Modal>

      {/* CoinPaprika Icon Search Modal */}
      <Modal
        title="Fetch Icon from CoinPaprika"
        open={iconDialogOpen}
        onCancel={() => {
          setIconDialogOpen(false);
          setIconSearchValue('');
        }}
        footer={null}
        destroyOnClose
      >
        <AutoComplete
          value={iconSearchValue}
          options={iconCpSearch.options}
          onSearch={val => {
            setIconSearchValue(val);
            iconCpSearch.search(val);
          }}
          onSelect={handleIconCpSelect}
          placeholder="Search CoinPaprika..."
          suffixIcon={<SearchOutlined />}
          notFoundContent="No results. Type at least 2 characters."
          style={{ width: '100%' }}
          autoFocus
        />
      </Modal>
    </>
  );
}
